import React, { useState, useEffect, useContext } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import useSwipe from "../hooks/useSwipe";
import { PerformerContext } from "../contexts/PerformerContext";
import {
  Box, Typography, Button, Chip, Avatar, Grid, Skeleton, Divider, Rating
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedIcon from "@mui/icons-material/Verified";
import axios from "axios";
import CamEmbedModal from "./CamEmbedModal";
import PerformerReviews from "./PerformerReviews";
import ChatBot from "./ChatBot";
import { PreRollAd } from "./TwinredPreRoll";
import { CATEGORIES } from "./CategoryPage";

const blink = keyframes`0%{opacity:0}50%{opacity:1}100%{opacity:0}`;
const LiveDot = styled("span")({ height:10, width:10, backgroundColor:"#4CAF50",
  borderRadius:"50%", display:"inline-block", marginRight:6,
  animation:`${blink} 1.5s ease-in-out infinite` });

const WatchBtn = styled(Button)({ background:"linear-gradient(45deg,#e91e63,#9c27b0)",
  borderRadius:"50px", color:"#fff", padding:"12px 32px", fontWeight:800,
  fontSize:"1rem", textTransform:"none", "&:hover":{ filter:"brightness(1.1)" } });

const PrivateBtn = styled(Button)({ background:"linear-gradient(45deg,#FF6B6B,#FF2BAA)",
  borderRadius:"50px", color:"#fff", padding:"12px 32px", fontWeight:800,
  fontSize:"1rem", textTransform:"none", "&:hover":{ filter:"brightness(1.1)" } });

const StatBox = styled(Box)({ textAlign:"center", px:2, py:1.5, bgcolor:"#f9f9f9",
  borderRadius:2, flex:1 });


// --- SEO Bio Generator ---
const capitalize = s => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

const generateBio = (p) => {
  if (!p) return '';
  const name = p.name || p.displayName || 'She';
  const age  = p.age  ? `${p.age}-year-old` : '';
  const eth  = capitalize(p.ethnicity) || '';
  const hair = p.hairColor ? `${capitalize(p.hairColor)}-haired` : '';
  const build = capitalize(p.build) || '';
  const lang  = p.language ? `speaks ${p.language}` : '';
  const tags  = (Array.isArray(p.fetishes) ? p.fetishes : (p.fetishes || '').split(','))
                  .map(f => f.trim()).filter(Boolean);
  const tagStr = tags.length > 0
    ? `She specializes in ${tags.slice(0, 5).join(', ')}${tags.length > 5 ? ' and more' : ''}.`
    : '';
  const intro = [age, eth, hair, build].filter(Boolean).join(' ');
  const langBit = lang ? `${name} ${lang} and welcomes fans from around the world.` : '';
  const gold = p.goldShow ? `${name} hosts Gold Shows — exclusive broadcast events with premium content.` : '';
  const hd   = p.hdStream ? 'Streams in crisp HD quality.' : '';

  return [
    `${name} is a${intro ? ` ${intro}` : ''} live cam model streaming right now on InstaCamGirls.`,
    tagStr,
    langBit,
    gold,
    hd,
    `Catch ${name} live, start a private show, or browse her full videos and photo gallery.`,
  ].filter(Boolean).join(' ');
};

const generateWhatToExpect = (p) => {
  if (!p) return [];
  const tags = (Array.isArray(p.fetishes) ? p.fetishes : (p.fetishes || '').split(','))
                 .map(f => f.trim()).filter(Boolean);
  const items = [];
  if (tags.length > 0) items.push({ icon: '🔥', label: 'Specialties', val: tags.slice(0, 6).join(', ') });
  if (p.theme)     items.push({ icon: '🎭', label: 'Show Style',   val: capitalize(p.theme) });
  if (p.language)  items.push({ icon: '🌍', label: 'Language',     val: p.language });
  if (p.zodiac)    items.push({ icon: '♾️', label: 'Zodiac',       val: capitalize(p.zodiac) });
  if (p.pubicHair) items.push({ icon: '✨', label: 'Pubic Hair',   val: capitalize(p.pubicHair) });
  if (p.bodyMods)  items.push({ icon: '💫', label: 'Body Mods',    val: capitalize(p.bodyMods) });
  if (p.goldShow)  items.push({ icon: '🥇', label: 'Gold Shows',   val: 'Available' });
  if (p.hdStream)  items.push({ icon: '📺', label: 'Stream Quality', val: 'HD' });
  return items;
};

const getCategoryLinksForPerformer = (p) => {
  if (!p) return [];
  const tags = (Array.isArray(p.fetishes) ? p.fetishes : (p.fetishes || "").split(","))
    .map(f => f.trim().toLowerCase());
  const eth = (p.ethnicity || "").toLowerCase();
  const links = [];

  const TAG_TO_SLUG = {
    "milf": "milf-cam-girls", "anal": "anal-cam-girls", "squirt": "squirt-cam-girls",
    "feet": "feet-cam-girls", "bdsm": "bdsm-cam-girls", "masturbation": "masturbation-cams",
    "striptease": "striptease-cams", "blowjob": "blowjob-cams", "deepthroat": "deepthroat-cams",
    "lovense": "lovense-cams", "oil": "oil-show-cams", "twerk": "twerk-cams",
    "ahegao": "ahegao-cams", "cowgirl": "cowgirl-cams", "topless": "topless-cams",
    "dirty-talk": "dirty-talk-cams", "roleplay": "roleplay-cam-girls",
    "dominant": "dominant-cam-girls", "submissive": "submissive-cam-girls",
    "tattoo": "tattoo-cam-girls", "bbw": "bbw-cam-girls", "amateur": "amateur-cam-girls",
    "voyeur": "voyeur-cam-girls", "natural": "natural-tits-cam-girls",
    "interactive": "lovense-cams", "dancing": "twerk-cams",
  };

  const ETH_TO_SLUG = {
    "hispanic": "latina-cam-girls", "latina": "latina-cam-girls",
    "asian": "asian-cam-girls", "ebony": "ebony-cam-girls", "black": "ebony-cam-girls",
    "indian": "indian-cam-girls", "middle eastern": "arab-cam-girls",
    "caucasian": "blonde-cam-girls",
  };

  tags.forEach(tag => {
    let slug = TAG_TO_SLUG[tag];
    if (!slug) {
      const key = Object.keys(TAG_TO_SLUG).find(k => tag.includes(k));
      if (key) slug = TAG_TO_SLUG[key];
    }
    if (slug && CATEGORIES[slug] && !links.find(l => l.slug === slug)) {
      links.push({ slug, label: CATEGORIES[slug].title });
    }
  });

  const ethSlug = ETH_TO_SLUG[eth];
  if (ethSlug && CATEGORIES[ethSlug] && !links.find(l => l.slug === ethSlug)) {
    links.push({ slug: ethSlug, label: CATEGORIES[ethSlug].title });
  }

  return links.slice(0, 8);
};

const PerformerProfilePage = () => {
  const { name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { performers: allLivePerformers } = useContext(PerformerContext);
  const [performer, setPerformer] = useState(null);
  const [related, setRelated]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [preRoll, setPreRoll]     = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [chatOpen, setChatOpen]   = useState(false);
  const [offlinePic, setOfflinePic] = useState(null);
  const [userTags, setUserTags]   = useState([]);
  const [tagInput, setTagInput]   = useState('');
  const [tagBusy, setTagBusy]     = useState(false);
  const [tagMsg, setTagMsg]       = useState('');
  const [s3Photos, setS3Photos]   = useState([]);

  // Load user-submitted tags for this performer
  useEffect(() => {
    if (!name) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/performer-tags/${encodeURIComponent(name)}`)
      .then(r => setUserTags(r.data || []))
      .catch(() => {});
  }, [name]);

  // Load S3-hosted photos (scraped from Streamate white-labels)
  useEffect(() => {
    if (!name) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/performer-photos/${encodeURIComponent(name)}`)
      .then(r => {
        const photos = Array.isArray(r.data) ? r.data : [];
        setS3Photos(photos.map(p => ({ full: p.s3_url, thumb: p.s3_url })));
      })
      .catch(() => {});
  }, [name]);

  // Auto-open chatbot after 3s on performer profile page
  useEffect(() => {
    const t = setTimeout(() => setChatOpen(true), 3000);
    return () => clearTimeout(t);
  }, [name]);

  // Pre-load offline performer pic from CDN (predictable URL by performer name)
  useEffect(() => {
    const cdnUrl = `https://imagetransform.icfcdn.com/profile/${encodeURIComponent(name)}.jpeg`;
    const img = new Image();
    img.onload = () => setOfflinePic(cdnUrl);
    img.onerror = () => setOfflinePic(null);
    img.src = cdnUrl;
  }, [name]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // If navigated from grid — show data immediately, then enrich in background
    if (location.state?.performer) {
      const p = location.state.performer;
      setPerformer({
        id: p.uniqueModelId || p.id,
        name: p.displayName || p.name,
        age: p.age, ethnicity: p.ethnicity, hairColor: p.hairColor,
        eyeColor: p.eyeColor, build: p.build, cupSize: p.cupSize,
        language: p.language, theme: p.theme, fetishes: p.fetishes,
        goldShow: p.goldShow, hdStream: p.hdStream, audio: p.audio,
        about: p.about || '', turnOns: p.turnOns || '', expertise: p.expertise || '',
        gallery: p.gallery || [],
        thumb: p.profilePictureUrl || '',
        full: p.profilePictureFullUrl || p.profilePictureUrl || '',
        chatRoomUrl: p.chatRoomUrl || `https://instacamgirlslive.com/cam/${p.displayName}/?AFNO=1-94033`,
        embedUrl: p.embedUrl || `https://hybridclient.naiadsystems.com/purecam?performer=${p.displayName}&widescreen=true`,
        displayName: p.displayName || p.name,
        uniqueModelId: p.uniqueModelId,
        profilePictureUrl: p.profilePictureUrl || '',
      });
      setLoading(false);
    }
    // Always fetch from API to get gallery + bio (or as primary fetch if no state)
    const fetchPerformer = async () => {
      if (!location.state?.performer) setLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/performers`, {
          params: { name, limit: 1 }
        });
        const list = Array.isArray(res.data) ? res.data : [];
        // If offline/not live — try the offline profile endpoint first (gets photos even when not streaming)
        if (list.length === 0) {
          try {
            const profileRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/performer-profile`, {
              params: { name }
            });
            const profileList = Array.isArray(profileRes.data) ? profileRes.data : [];
            if (profileList.length > 0) {
              const p = profileList[0];
              const a = p["@attributes"] || {};
              const gallery = (() => {
                const raw = p?.Media?.PhotoGallery?.Pic;
                if (!raw) return [];
                return (Array.isArray(raw) ? raw : [raw]).map(pic => ({
                  full:  pic?.Full?.["@attributes"]?.Src  || "",
                  thumb: pic?.Thumb?.["@attributes"]?.Src || "",
                })).filter(x => x.full);
              })();
              const desc = p?.Descriptions || {};
              const safeStr = v => Array.isArray(v) ? '' : (v || '');
              setPerformer({
                id: a.Id, name: a.Name, age: a.Age,
                ethnicity: a.Ethnicity, hairColor: a.HairColor,
                eyeColor: a.EyeColor, build: a.Build, cupSize: a.CupSize,
                language: a.Language, theme: a.Theme,
                fetishes: a.Fetishes ? (Array.isArray(a.Fetishes) ? a.Fetishes : a.Fetishes.split(',').map(f => f.trim())) : [],
                zodiac: a.Zodiac, bodyMods: a.BodyMods, pubicHair: a.PubicHair,
                goldShow: false, hdStream: false, audio: false,
                about: safeStr(desc.About), turnOns: safeStr(desc.TurnOns), expertise: safeStr(desc.Expertise),
                gallery,
                thumb: p?.Media?.Pic?.Thumb?.["@attributes"]?.Src || "",
                full:  p?.Media?.Pic?.Full?.["@attributes"]?.Src  || "",
                chatRoomUrl: `https://instacamgirlslive.com/cam/${a.Name}/?AFNO=1-94033`,
                embedUrl: `https://hybridclient.naiadsystems.com/purecam?performer=${a.Name}&widescreen=true`,
                displayName: a.Name, uniqueModelId: a.Id,
                profilePictureUrl: p?.Media?.Pic?.Thumb?.["@attributes"]?.Src || "",
                isOffline: true,
              });
              setLoading(false);
              return; // profile loaded offline — skip localStorage fallback
            }
          } catch(e) {}
        }

        // Final fallback: try localStorage cache from last grid load
        if (list.length === 0 && !location.state?.performer) {
          try {
            const cache = JSON.parse(localStorage.getItem('icg_performer_cache') || '{}');
            const cached = cache[name];
            if (cached) {
              setPerformer({
                id: cached.uniqueModelId,
                name: cached.displayName,
                age: cached.age, ethnicity: cached.ethnicity, hairColor: cached.hairColor,
                eyeColor: cached.eyeColor, build: cached.build, cupSize: cached.cupSize,
                language: cached.language, theme: cached.theme, fetishes: cached.fetishes,
                goldShow: cached.goldShow, hdStream: cached.hdStream, audio: cached.audio,
                about: cached.about || '', turnOns: cached.turnOns || '', expertise: cached.expertise || '',
                gallery: cached.gallery || [],
                thumb: cached.profilePictureUrl || '',
                full: cached.profilePictureFullUrl || cached.profilePictureUrl || '',
                chatRoomUrl: cached.chatRoomUrl || `https://instacamgirlslive.com/cam/${cached.displayName}/?AFNO=1-94033`,
                embedUrl: cached.embedUrl || `https://hybridclient.naiadsystems.com/purecam?performer=${cached.displayName}&widescreen=true`,
                displayName: cached.displayName,
                uniqueModelId: cached.uniqueModelId,
                profilePictureUrl: cached.profilePictureUrl || '',
                isOffline: true,
              });
            }
          } catch(e) {}
        }
        if (list.length > 0) {
          const p = list[0];
          const a = p["@attributes"] || {};
          const gallery = (() => {
            const raw = p?.Media?.PhotoGallery?.Pic;
            if (!raw) return [];
            return (Array.isArray(raw) ? raw : [raw]).map(pic => ({
              full:  pic?.Full?.["@attributes"]?.Src  || "",
              thumb: pic?.Thumb?.["@attributes"]?.Src || "",
            })).filter(x => x.full);
          })();
          const desc = p?.Descriptions || {};
          const safeStr = v => Array.isArray(v) ? '' : (v || '');
          setPerformer({
            id: a.Id, name: a.Name, age: a.Age,
            ethnicity: a.Ethnicity, hairColor: a.HairColor,
            eyeColor: a.EyeColor, build: a.Build, cupSize: a.CupSize,
            language: a.Language, theme: a.Theme, fetishes: a.Fetishes ? (Array.isArray(a.Fetishes) ? a.Fetishes : a.Fetishes.split(',').map(f => f.trim())) : [],
            zodiac: a.Zodiac, bodyMods: a.BodyMods, pubicHair: a.PubicHair,
            goldShow: !!(a.PerfFlag && (parseInt(a.PerfFlag) & 0x00100000)),
            hdStream: !!(a.PerfFlag && (parseInt(a.PerfFlag) & 0x10000000)),
            audio: a.Audio === "true",
            about: safeStr(desc.About), turnOns: safeStr(desc.TurnOns), expertise: safeStr(desc.Expertise),
            gallery,
            thumb: p?.Media?.Pic?.Thumb?.["@attributes"]?.Src || "",
            full:  p?.Media?.Pic?.Full?.["@attributes"]?.Src  || "",
            chatRoomUrl: `https://instacamgirlslive.com/cam/${a.Name}/?smid=${a.Id}&AFNO=1-94033`,
            embedUrl: `https://hybridclient.naiadsystems.com/purecam?performer=${a.Name}&widescreen=true`,
            displayName: a.Name, uniqueModelId: a.Id,
            profilePictureUrl: p?.Media?.Pic?.Thumb?.["@attributes"]?.Src || "",
          });
          if (a.Ethnicity) {
            const relRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/performers`, {
              params: { ethnicity: a.Ethnicity, limit: 6 }
            });
            setRelated((Array.isArray(relRes.data) ? relRes.data : [])
              .filter(x => x["@attributes"]?.Name !== a.Name)
              .slice(0, 6)
              .map(x => ({ name: x["@attributes"]?.Name, thumb: x?.Media?.Pic?.Thumb?.["@attributes"]?.Src }))
            );
          }
        }
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    fetchPerformer();
  }, [name]); // eslint-disable-line

  // Update page SEO
  useEffect(() => {
    if (!performer) return;
    const pname = performer.name || performer.displayName || "Cam Model";

    // Build a richer, more click-worthy title based on performer attributes
    const eth  = (performer.ethnicity || '').toLowerCase();
    const hair = (performer.hairColor  || '').toLowerCase();
    const age  = parseInt(performer.age) || 0;
    const ETH_LABEL  = { hispanic:'Latina', ebony:'Ebony', asian:'Asian', caucasian:'', middleeastern:'Arab', indian:'Indian' };
    const HAIR_LABEL = { blond:'Blonde', brown:'Brunette', red:'Redhead', black:'Dark-Haired', 'dark brown':'Brunette' };
    const ethLabel  = ETH_LABEL[eth]  || '';
    const hairLabel = HAIR_LABEL[hair] || '';
    const ageLabel  = age >= 40 ? 'MILF ' : (age >= 18 && age <= 22 ? 'Young ' : '');
    const qualifier = ethLabel || (ageLabel + hairLabel) || 'Cam Girl';
    const titleSuffix = qualifier && qualifier !== 'Cam Girl' ? ` — ${qualifier} Cam Girl` : ' Live Cam';
    const pageTitle = `${pname}${titleSuffix} | Watch Free on InstaCamGirls`;
    document.title = pageTitle;

    // Rich meta description — include ethnicity, age, key traits for CTR
    const fetishList = (Array.isArray(performer.fetishes)
      ? performer.fetishes
      : (performer.fetishes || '').split(',')
    ).map(f => f.trim()).filter(Boolean).slice(0, 3);
    const traitStr = [ethLabel, hairLabel, age ? `${age} yrs` : '', ...fetishList].filter(Boolean).join(', ');
    const liveOrOff = performer.isOffline ? 'View her profile' : 'She is live now';

    // Meta description
    const setMeta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement('meta'); prop ? el.setAttribute('property', name) : el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const descText = (`Watch ${pname} on InstaCamGirls — free cam show, private shows, tips and more.${traitStr ? ' ' + traitStr + '.' : ''} ${liveOrOff}!`).slice(0, 155);
    setMeta('description', descText);
    setMeta('og:title', pageTitle, true);
    setMeta('og:description', descText, true);
    setMeta('og:type', 'profile', true);
    if (performer.thumb) setMeta('og:image', performer.thumb, true);

    // Canonical
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `https://instacamgirls.com/cam/${encodeURIComponent(pname)}`;

    // JSON-LD — Person schema (client-side fallback; prerender injects at build time)
    const knowsAbout = (Array.isArray(performer.fetishes)
      ? performer.fetishes
      : (performer.fetishes || "").split(",").map(f => f.trim())
    ).filter(Boolean);
    const sameAs = [performer.chatRoomUrl].filter(Boolean);

    const ldId = 'ld-performer';
    let ldEl = document.getElementById(ldId);
    if (!ldEl) { ldEl = document.createElement('script'); ldEl.id = ldId; ldEl.type = 'application/ld+json'; document.head.appendChild(ldEl); }
    ldEl.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": pname,
      "url": `https://instacamgirls.com/cam/${encodeURIComponent(pname)}`,
      "image": performer.profilePictureUrl || performer.full || performer.thumb || undefined,
      "description": generateBio(performer),
      "jobTitle": "Live Cam Performer",
      "worksFor": { "@type": "Organization", "name": "InstaCamGirls", "url": "https://instacamgirls.com" },
      "sameAs": sameAs,
      "knowsAbout": knowsAbout,
    });
  }, [performer]);

  const handleAddTags = async (e) => {
    e.preventDefault();
    if (!tagInput.trim()) return;
    setTagBusy(true);
    setTagMsg('');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/performer-tags/${encodeURIComponent(name)}`,
        { tags: tagInput }
      );
      setUserTags(res.data.tags || []);
      setTagInput('');
      const saved = res.data.saved || [];
      const errs  = res.data.errors || [];
      if (saved.length > 0) setTagMsg(`✅ Added: ${saved.join(', ')}${errs.length ? ` | ⚠️ Skipped: ${errs.join(', ')}` : ''}`);
      else setTagMsg(`⚠️ ${errs.join(' | ')}`);
      setTimeout(() => setTagMsg(''), 4000);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save tags';
      setTagMsg(`⚠️ ${msg}`);
      setTimeout(() => setTagMsg(''), 4000);
    }
    setTagBusy(false);
  };

  // Swipe navigation — must be declared before any early returns (Rules of Hooks)
  // Values depend on `related` which is initialized as [] so safe to use here
  const nextRelatedEarly = related[0];
  const swipeHandlers = useSwipe(
    () => { if (nextRelatedEarly) navigate(`/cam/${nextRelatedEarly.name}`); },
    () => navigate(-1)
  );

  if (loading) return (
    <Box sx={{ p: 4, maxWidth: 900, mx: "auto" }}>
      <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mb: 2 }} />
      <Skeleton width="60%" height={40} sx={{ mb: 1 }} />
      <Skeleton width="40%" height={30} />
    </Box>
  );

  if (!performer) return (
    <Box sx={{ minHeight: "60vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", textAlign: "center", p: 4 }}>
      <Box sx={{ fontSize: 64, mb: 2 }}>😴</Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        {name} is currently offline
      </Typography>
      <Typography sx={{ color: "#666", mb: 1, maxWidth: 400, fontSize: "1rem" }}>
        She's not live right now — but her exclusive videos are available 🎬
      </Typography>

      {/* Teaser photo — blurred profile pic to entice click */}
      {offlinePic && (
        <Box sx={{ position: "relative", width: 260, height: 320, mx: "auto", mb: 2, mt: 1,
          borderRadius: 3, overflow: "hidden", cursor: "pointer",
          boxShadow: "0 8px 32px rgba(255,65,150,0.4)",
          border: "3px solid rgba(255,65,150,0.5)" }}
          onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(name)}/?AFNO=1-70001`, '_blank')}>
          <Box component="img" src={offlinePic} alt={`${name} exclusive content`}
            sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center",
              filter: "blur(5px) brightness(0.65)", transform: "scale(1.08)" }} />
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            <Box sx={{ fontSize: 30 }}>🔒</Box>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "0.82rem",
              textShadow: "0 2px 8px rgba(0,0,0,0.9)", textAlign: "center", px: 1 }}>
              Unlock Her Content
            </Typography>
          </Box>
        </Box>
      )}

      {/* Cambuilder CTA — videos upsell */}
      <Box
        onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(name)}/?AFNO=1-70001`, '_blank')}
        sx={{ mt: 1, mb: 3, mx: "auto", maxWidth: 400, width: "100%",
          background: "linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)",
          borderRadius: 3, p: 3, cursor: "pointer", position: "relative", overflow: "hidden",
          border: "1px solid rgba(255,65,150,0.3)",
          boxShadow: "0 8px 32px rgba(255,65,150,0.2)",
          "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 40px rgba(255,65,150,0.35)" },
          transition: "0.25s" }}>
        <Box sx={{ position: "absolute", top: -20, right: -20, fontSize: 80, opacity: 0.08 }}>🎬</Box>
        <Typography sx={{ color: "#FF4196", fontWeight: 800, fontSize: "0.75rem",
          letterSpacing: 2, textTransform: "uppercase", mb: 0.5 }}>
          🎬 Exclusive Content
        </Typography>
        <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.15rem", mb: 0.5 }}>
          Watch {name}'s Videos
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", mb: 1.5 }}>
          Private shows, full-length recordings & more
        </Typography>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1,
          bgcolor: "#FF4196", borderRadius: "50px", px: 2.5, py: 0.8 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem" }}>
            Browse Her Videos →
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
        <Button component={Link} to="/"
          variant="contained"
          sx={{ background: "linear-gradient(45deg,#e91e63,#9c27b0)", borderRadius: "50px",
            textTransform: "none", fontWeight: 700, px: 4 }}>
          See Who's Live Now
        </Button>
        <Button
          variant="outlined"
          onClick={() => window.open(`https://instacamgirlslive.com/?AFNO=1-70001`, '_blank')}
          sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700,
            borderColor: "#e91e63", color: "#e91e63", px: 4 }}>
          Browse All Cams
        </Button>
      </Box>

      {/* Cam Tab Overlay — live girls widget while this performer is offline */}
      <Box sx={{ width: "100%", maxWidth: 1170, mt: 4, mb: 2, overflowX: "auto" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, textAlign: "left", fontSize: "1rem" }}>
          🔴 Girls Live Right Now
        </Typography>
        <Box sx={{ minWidth: 320 }}>
          <iframe
            src="https://as.sexad.net/as/if?p=reseller&w=1&h=1&v=10574&show_girl_cams=true&show_boy_cams=false&show_trans_cams=false&bg_color=FFFFFF&title_color=333333&sidebar_font_color=333333&tag_bg_color=FF4196&tag_font_color=FFFFFF&tag_border_color=FF4196&tag_border_radius=4px&cta_bg_color=FF4196&cta_font_color=FFFFFF&cta_border_color=e0337f&cta_border_radius=4px&show_cta_buttons=true&AFNO=1-70001&wl=instacamgirls.net"
            allowTransparency="true"
            style={{ border: "none", margin: 0, padding: 0, width: "100%", minWidth: 320, height: 470 }}
            frameBorder="0"
            marginHeight="0"
            marginWidth="0"
            scrolling="no"
            title="Live Cam Girls"
          />
        </Box>
      </Box>

      {/* Reviews still shown even if offline */}
      <Box sx={{ width: "100%", maxWidth: 600, mt: 2 }}>
        <PerformerReviews performerName={name} />
      </Box>
    </Box>
  );

  // Merge: S3 scraped photos take priority, fill with Streamate gallery, fallback to profile pic
  const allPhotos = (() => {
    if (s3Photos.length > 0) return s3Photos;
    if (performer.gallery.length > 0) return performer.gallery;
    return [{ full: performer.full, thumb: performer.thumb }];
  })();

  // Swipe navigation handlers already declared above (before early returns)
  const nextRelated = related[0];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      {preRoll && <PreRollAd onComplete={() => {
        // After pre-roll, open Streamate directly
        const url = performer.chatRoomUrl || `https://instacamgirlslive.com/cam/${encodeURIComponent(performer.name)}/?AFNO=1-94033`;
        window.open(url, '_blank');
        setPreRoll(false);
      }} />}
      {false && embedOpen && (
        <CamEmbedModal
          performer={performer}
          allPerformers={
            // Use full live grid as pool — start from current performer's position
            (() => {
              const pool = allLivePerformers.length > 1 ? allLivePerformers : [
                performer,
                ...related.map(r => ({ displayName: r.name, name: r.name, profilePictureUrl: r.thumb, uniqueModelId: r.name }))
              ];
              // Put current performer first so next/prev are relative to her
              const idx = pool.findIndex(p => (p.displayName || p.name) === performer.name);
              if (idx > 0) return [...pool.slice(idx), ...pool.slice(0, idx)];
              return pool;
            })()
          }
          onClose={() => setEmbedOpen(false)}
          onNext={(nextPerformer) => {
            setEmbedOpen(false);
            navigate(`/cam/${nextPerformer.displayName || nextPerformer.name}`);
          }}
        />
      )}
      <ChatBot
        key={performer?.name}
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        performer={performer}
        performerName={performer?.name}
      />

      {/* Back */}
      <Box sx={{ px: { xs: 2, md: 6 }, pt: 2 }}>
        <Button component={Link} to="/" startIcon={<ArrowBackIcon />} sx={{ color: "#FF4196" }}>All Performers</Button>
      </Box>

      <Box {...swipeHandlers} sx={{ maxWidth: 960, mx: "auto", px: { xs: 1, md: 3 }, py: 2, touchAction: 'pan-y', overflowX: 'hidden' }}>
        <Grid container spacing={3}>
          {/* Left: Photos */}
          <Grid item xs={12} md={5}>
            {/* Main photo */}
            <Box sx={{ borderRadius: 2, overflow: "hidden", mb: 1, position: "relative", cursor: "pointer" }}
              onClick={() => setPreRoll(true)}>
              <Box component="img"
                src={allPhotos[activePhoto]?.full || performer.full}
                alt={`${performer.name} live cam`}
                sx={{ width: "100%", objectFit: "cover", maxHeight: 480, display: "block" }}
              />
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center",
                justifyContent: "center", bgcolor: "rgba(0,0,0,0)", transition: "0.2s",
                "&:hover": { bgcolor: "rgba(0,0,0,0.2)" } }}>
                <LiveTvIcon sx={{ color: "rgba(255,255,255,0.8)", fontSize: 64, opacity: 0 ,
                  ".MuiBox-root:hover &": { opacity: 1 } }} />
              </Box>
              {performer.isOffline ? (
                <Box sx={{ position: "absolute", top: 12, left: 12, bgcolor: "rgba(100,100,100,0.85)", borderRadius: "20px", px: 1.5, py: 0.5 }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem" }}>😴 Currently Offline</Typography>
                </Box>
              ) : (
                <Box sx={{ position: "absolute", top: 12, left: 12, display: "flex", alignItems: "center",
                  bgcolor: "rgba(76,175,80,0.9)", borderRadius: "20px", px: 1.5, py: 0.5, gap: 0.5 }}>
                  <LiveDot /><Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem" }}>LIVE NOW</Typography>
                </Box>
              )}
            </Box>

            {/* Thumbnail strip */}
            {allPhotos.length > 1 && (
              <Box sx={{ display: "flex", gap: 0.8, overflowX: "auto", pb: 0.5 }}>
                {allPhotos.map((p, i) => (
                  <Box key={i} onClick={() => setActivePhoto(i)}
                    sx={{ width: 60, height: 60, borderRadius: 1, overflow: "hidden", flexShrink: 0,
                      cursor: "pointer", border: i === activePhoto ? "2px solid #FF4196" : "2px solid transparent" }}>
                    <Box component="img" src={p.thumb || p.full} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </Box>
                ))}
              </Box>
            )}

            {/* CTAs */}
            <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
              {/* 📸 Videos & Photos — scrolls to gallery section below */}
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  const el = document.getElementById('photo-gallery');
                  if (el) {
                    const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
                    window.scrollTo({ top, behavior: 'smooth' });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.95rem",
                  py: 1.4,
                  background: "linear-gradient(135deg, #FF4196, #e91e63)",
                  color: "#fff",
                  boxShadow: "0 4px 20px rgba(255,65,150,0.4)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #e91e63, #c2185b)",
                  },
                  transition: "0.2s"
                }}
              >
                📸 Videos &amp; Photos
              </Button>

              <WatchBtn variant="contained" fullWidth onClick={() => setPreRoll(true)} startIcon={<LiveTvIcon />}>
                Watch Live — Free
              </WatchBtn>
              <PrivateBtn variant="contained" fullWidth
                onClick={() => window.open(performer.chatRoomUrl, "_blank")} startIcon={<LockIcon />}>
                Start Private Show
              </PrivateBtn>

              {/* Chat CTA — prominent on mobile */}
              <Box
                onClick={() => setChatOpen(true)}
                sx={{ width: "100%", mt: 0.5, py: 1.5, borderRadius: "50px", cursor: "pointer",
                  background: "linear-gradient(45deg,#1a1a2e,#FF4196)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
                  boxShadow: "0 4px 20px rgba(255,65,150,0.3)",
                  "&:hover": { filter: "brightness(1.1)" }, transition: "0.2s" }}>
                <span style={{ fontSize: 20 }}>💬</span>
                <span style={{ color: "#fff", fontWeight: 800, fontSize: "1rem" }}>
                  Chat with {performer.name}
                </span>
              </Box>
            </Box>

            {/* ── Content Slider — Photos & Videos upsell (desktop dead space) ── */}
            {allPhotos.length > 1 && (
              <Box sx={{ mt: 3, display: { xs: "none", md: "block" } }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#222" }}>
                    📸 Photos &amp; Videos
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(performer.name)}/?AFNO=1-70001`, '_blank')}
                    sx={{
                      borderRadius: "50px", textTransform: "none", fontWeight: 700,
                      fontSize: "0.75rem", py: 0.4, px: 1.5,
                      background: "linear-gradient(135deg,#FF4196,#e91e63)",
                      color: "#fff",
                      "&:hover": { filter: "brightness(1.1)" },
                    }}
                  >
                    🔓 Unlock All
                  </Button>
                </Box>

                {/* Slider strip */}
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                }}>
                  {allPhotos.slice(0, 6).map((p, i) => (
                    <Box
                      key={i}
                      onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(performer.name)}/?AFNO=1-70001`, '_blank')}
                      sx={{
                        position: "relative", borderRadius: 1.5, overflow: "hidden",
                        aspectRatio: "3/4", cursor: "pointer",
                        "&:hover img": { transform: "scale(1.06)" },
                        "&:hover .slide-overlay": { opacity: 1 },
                      }}
                    >
                      <Box
                        component="img"
                        src={p.thumb || p.full}
                        alt={`${performer.name} ${i + 1}`}
                        sx={{
                          width: "100%", height: "100%", objectFit: "cover",
                          display: "block", transition: "transform 0.3s ease",
                        }}
                      />
                      {/* Blur + lock on photos after the first two */}
                      {i >= 2 && (
                        <Box sx={{
                          position: "absolute", inset: 0,
                          backdropFilter: "blur(7px)",
                          WebkitBackdropFilter: "blur(7px)",
                          bgcolor: "rgba(0,0,0,0.4)",
                          display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center", gap: 0.3,
                        }}>
                          <Typography sx={{ fontSize: "1.3rem" }}>🔒</Typography>
                          <Typography sx={{ color: "#fff", fontSize: "0.65rem", fontWeight: 700 }}>
                            Unlock
                          </Typography>
                        </Box>
                      )}
                      <Box className="slide-overlay" sx={{
                        position: "absolute", inset: 0, opacity: 0, transition: "0.2s",
                        bgcolor: "rgba(255,65,150,0.2)",
                      }} />
                    </Box>
                  ))}
                </Box>

                {/* Download / Buy CTA */}
                <Box
                  onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(performer.name)}/?AFNO=1-70001`, '_blank')}
                  sx={{
                    mt: 1.5, p: 2, borderRadius: 2, cursor: "pointer",
                    background: "linear-gradient(135deg,#1a1a2e 0%,#16213e 60%,#0f3460 100%)",
                    border: "1px solid rgba(255,65,150,0.35)",
                    boxShadow: "0 4px 20px rgba(255,65,150,0.18)",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5,
                    "&:hover": { boxShadow: "0 6px 28px rgba(255,65,150,0.35)", transform: "translateY(-1px)" },
                    transition: "0.2s",
                  }}
                >
                  <Box>
                    <Typography sx={{ color: "#FF4196", fontWeight: 800, fontSize: "0.7rem",
                      letterSpacing: 1.5, textTransform: "uppercase", mb: 0.3 }}>
                      🎬 Exclusive Content
                    </Typography>
                    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "0.95rem" }}>
                      Buy {performer.name}'s Videos &amp; Photos
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.78rem" }}>
                      Full-length recordings, private shows &amp; more
                    </Typography>
                  </Box>
                  <Box sx={{
                    bgcolor: "#FF4196", borderRadius: "50px", px: 2, py: 0.8, flexShrink: 0,
                    display: "flex", alignItems: "center", gap: 0.5,
                  }}>
                    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                      Get Access →
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

          </Grid>

          {/* Right: Info */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
              <Avatar src={performer.thumb} sx={{ width: 60, height: 60 }} />
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{performer.name}</Typography>
                  <VerifiedIcon sx={{ color: "#FF4196", fontSize: 22 }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                  {performer.goldShow && <Chip label="🥇 Gold Show" size="small" sx={{ bgcolor: "#FFD700", color: "#000", fontWeight: 800 }} />}
                  {performer.hdStream && <Chip label="HD" size="small" sx={{ bgcolor: "#2196F3", color: "#fff", fontWeight: 700 }} />}
                  {performer.audio && <Chip label="🔊 Audio" size="small" sx={{ bgcolor: "#f0f0f0", fontWeight: 600 }} />}
                </Box>
              </Box>
            </Box>

            {/* Quick stats */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              {performer.age && <StatBox><Typography variant="h6" sx={{ fontWeight: 800 }}>{performer.age}</Typography><Typography variant="caption" sx={{ color: "#888" }}>Age</Typography></StatBox>}
              {performer.ethnicity && <StatBox><Typography variant="h6" sx={{ fontWeight: 800, textTransform: "capitalize" }}>{performer.ethnicity}</Typography><Typography variant="caption" sx={{ color: "#888" }}>Ethnicity</Typography></StatBox>}
              {performer.hairColor && <StatBox><Typography variant="h6" sx={{ fontWeight: 800, textTransform: "capitalize" }}>{performer.hairColor}</Typography><Typography variant="caption" sx={{ color: "#888" }}>Hair</Typography></StatBox>}
              {performer.build && <StatBox><Typography variant="h6" sx={{ fontWeight: 800, textTransform: "capitalize" }}>{performer.build}</Typography><Typography variant="caption" sx={{ color: "#888" }}>Build</Typography></StatBox>}
            </Box>

            {/* Auto-generated SEO bio — always shown */}
            <Box sx={{ mb: 2.5, p: 2, bgcolor: "#fef9ff", borderRadius: 2, border: "1px solid #f3e5f5" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: "#333" }}>
                About {performer.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.85, fontSize: "0.92rem" }}>
                {generateBio(performer)}
              </Typography>
            </Box>

            {/* What to Expect — generated from performer attributes */}
            {generateWhatToExpect(performer).length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: "#333" }}>
                  What to Expect
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  {generateWhatToExpect(performer).map(({ icon, label, val }) => (
                    <Box key={label} sx={{ display: "flex", alignItems: "flex-start", gap: 1,
                      p: 1.2, bgcolor: "#fafafa", borderRadius: 2, border: "1px solid #f0f0f0" }}>
                      <span style={{ fontSize: 16, lineHeight: 1.4 }}>{icon}</span>
                      <Box>
                        <Typography sx={{ fontSize: "0.7rem", color: "#999", display: "block" }}>{label}</Typography>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#333",
                          textTransform: "capitalize" }}>{val}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Real bio from API — shown if available */}
            {performer.about && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>In Her Own Words</Typography>
                <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.7 }}>{performer.about}</Typography>
              </Box>
            )}

            {performer.turnOns && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Turn Ons</Typography>
                <Typography variant="body2" sx={{ color: "#555" }}>{performer.turnOns}</Typography>
              </Box>
            )}

            {performer.expertise && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Expertise</Typography>
                <Typography variant="body2" sx={{ color: "#555" }}>{performer.expertise}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Details grid */}
            <Grid container spacing={1}>
              {[
                ["Eye Color",  performer.eyeColor],
                ["Cup Size",   performer.cupSize],
              ].filter(([,v]) => v).map(([label, val]) => (
                <Grid item xs={6} key={label}>
                  <Typography variant="caption" sx={{ color: "#aaa", display: "block" }}>{label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textTransform: "capitalize" }}>{val}</Typography>
                </Grid>
              ))}
            </Grid>

            {/* Tags — performer fetishes + user-submitted crowd-sourced tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#555" }}>
                🏷️ Tags
              </Typography>

              {/* Performer fetish tags */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 1 }}>
                {(Array.isArray(performer.fetishes) ? performer.fetishes : (performer.fetishes || "").split(","))
                  .map(f => f.trim()).filter(Boolean)
                  .map(f => (
                    <Chip key={`fetish-${f}`} label={`#${f}`} size="small"
                      component={Link} to={`/?tag=${encodeURIComponent(f.toLowerCase())}`} clickable
                      sx={{ bgcolor: "#fff0f7", color: "#FF4196", border: "1px solid #FF4196",
                        fontSize: "0.72rem", textDecoration: "none",
                        "&:hover": { bgcolor: "#FF4196", color: "#fff" }, transition: "0.15s" }} />
                  ))}
                {/* User-submitted tags (sorted by vote count) */}
                {userTags.map(t => (
                  <Chip key={`utag-${t.tag}`}
                    label={`#${t.tag}${t.count > 1 ? ` ${t.count}` : ''}`}
                    size="small"
                    component={Link} to={`/?tag=${encodeURIComponent(t.tag)}`} clickable
                    sx={{ bgcolor: "#f0f4ff", color: "#5c6bc0", border: "1px solid #9fa8da",
                      fontSize: "0.72rem", textDecoration: "none",
                      "&:hover": { bgcolor: "#5c6bc0", color: "#fff" }, transition: "0.15s" }} />
                ))}
              </Box>

              {/* Internal category links for SEO */}
              {getCategoryLinksForPerformer(performer).length > 0 && (
                <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  <Typography variant="caption" sx={{ color: "#999", width: "100%", mb: 0.5 }}>
                    Browse similar models:
                  </Typography>
                  {getCategoryLinksForPerformer(performer).map(({ slug, label }) => (
                    <Chip
                      key={slug}
                      label={label}
                      component={Link}
                      to={`/cams/${slug}`}
                      clickable
                      size="small"
                      sx={{ bgcolor: "#f0f0f0", fontSize: "0.75rem" }}
                    />
                  ))}
                </Box>
              )}

              {/* Add tags form */}
              <Box component="form" onSubmit={handleAddTags}
                sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1.5,
                  p: 1.5, bgcolor: "#fafafa", borderRadius: 2, border: "1px solid #eee" }}>
                <Box sx={{ flex: 1 }}>
                  <input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    placeholder="Add tags (e.g. sexy, playful, dom) — 3–15 chars, comma-separated"
                    style={{
                      width: "100%", border: "1px solid #ddd", borderRadius: 20,
                      padding: "8px 14px", fontSize: "0.82rem", outline: "none",
                      fontFamily: "inherit", background: "#fff", boxSizing: "border-box",
                    }}
                    maxLength={160}
                  />
                </Box>
                <button type="submit" disabled={tagBusy || !tagInput.trim()}
                  style={{
                    background: tagBusy ? "#ccc" : "#FF4196",
                    border: "none", borderRadius: 20, color: "#fff",
                    padding: "8px 18px", fontWeight: 700, fontSize: "0.82rem",
                    cursor: tagBusy ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                    fontFamily: "inherit"
                  }}>
                  {tagBusy ? "…" : "Add"}
                </button>
              </Box>
              {tagMsg && (
                <Typography sx={{ fontSize: "0.78rem", mt: 0.8,
                  color: tagMsg.startsWith('✅') ? "#4CAF50" : "#FF9800" }}>
                  {tagMsg}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* ── Photo Gallery ─────────────────────────────────────────────────── */}
        {allPhotos.length >= 1 && (
          <Box id="photo-gallery" sx={{ mt: 5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                📸 Photos &amp; Videos
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(performer.name)}/?AFNO=1-70001`, '_blank')}
                sx={{
                  borderRadius: "50px", textTransform: "none", fontWeight: 700,
                  fontSize: "0.82rem", py: 0.6, px: 2,
                  background: "linear-gradient(135deg, #FF4196, #e91e63)",
                  "&:hover": { background: "linear-gradient(135deg, #e91e63, #c2185b)" },
                }}
              >
                🔓 Unlock exclusive content
              </Button>
            </Box>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(4, 1fr)", md: "repeat(5, 1fr)" },
              gap: 1,
            }}>
              {allPhotos.map((p, i) => (
                <Box
                  key={i}
                  onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(performer.name)}/?AFNO=1-70001`, '_blank')}
                  sx={{
                    position: "relative", borderRadius: 1.5, overflow: "hidden",
                    aspectRatio: "3/4", cursor: "pointer",
                    "&:hover .overlay": { opacity: 1 },
                    "&:hover img": { transform: "scale(1.05)" },
                  }}
                >
                  <Box
                    component="img"
                    src={p.thumb || p.full}
                    alt={`${performer.name} photo ${i + 1}`}
                    sx={{
                      width: "100%", height: "100%", objectFit: "cover", display: "block",
                      transition: "transform 0.3s ease",
                    }}
                  />
                  {/* Blur + lock overlay on photos after index 2 */}
                  {i >= 3 && (
                    <Box sx={{
                      position: "absolute", inset: 0,
                      backdropFilter: "blur(6px)",
                      WebkitBackdropFilter: "blur(6px)",
                      bgcolor: "rgba(0,0,0,0.35)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Typography sx={{ fontSize: "1.6rem" }}>🔒</Typography>
                    </Box>
                  )}
                  <Box className="overlay" sx={{
                    position: "absolute", inset: 0, opacity: 0, transition: "0.2s",
                    bgcolor: "rgba(255,65,150,0.25)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "0.75rem" }}>View</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => window.open(`https://instacamgirlslive.com/cam/${encodeURIComponent(performer.name)}/?AFNO=1-70001`, '_blank')}
                sx={{
                  borderRadius: "50px", textTransform: "none", fontWeight: 700,
                  borderColor: "#FF4196", color: "#FF4196", borderWidth: 2,
                  py: 1.2, maxWidth: 400,
                  "&:hover": { bgcolor: "#FF4196", color: "#fff", borderColor: "#FF4196" },
                }}
              >
                🔓 Unlock all photos &amp; videos on her profile
              </Button>
            </Box>
          </Box>
        )}

        {/* Related performers + category link */}
        <Box sx={{ mt: 5 }}>
          {related.length > 0 && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  More {capitalize(performer.ethnicity) || ''} Performers
                </Typography>
                {performer.ethnicity && (
                  <Button component={Link}
                    to={`/category/${performer.ethnicity.toLowerCase()}`}
                    sx={{ color: "#FF4196", textTransform: "none", fontWeight: 600, fontSize: "0.85rem" }}>
                    See all →
                  </Button>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1 }}>
                {related.map(r => (
                  <Box key={r.name} component={Link} to={`/cam/${r.name}`}
                    sx={{ textDecoration: "none", textAlign: "center", flexShrink: 0 }}>
                    <Avatar src={r.thumb} sx={{ width: 72, height: 72, mx: "auto", mb: 0.5,
                      border: "2px solid #FF4196" }} />
                    <Typography variant="caption" sx={{ color: "#333", fontWeight: 600,
                      display: "block", maxWidth: 72, overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap" }}>{r.name}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Tag cloud — explore by interest */}
          {(Array.isArray(performer.fetishes) ? performer.fetishes : (performer.fetishes || '').split(','))
            .map(f => f.trim()).filter(Boolean).length > 0 && (
            <Box sx={{ mt: 4, p: 2.5, bgcolor: "#fafafa", borderRadius: 2, border: "1px solid #f0f0f0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Explore by Interest
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {(Array.isArray(performer.fetishes) ? performer.fetishes : (performer.fetishes || '').split(','))
                  .map(f => f.trim()).filter(Boolean)
                  .map(f => (
                    <Chip key={f} label={f}
                      component={Link}
                      to={`/?tag=${encodeURIComponent(f.toLowerCase())}`}
                      clickable
                      sx={{ bgcolor: "#fff", border: "1px solid #ddd", color: "#555",
                        fontSize: "0.82rem", fontWeight: 500,
                        "&:hover": { bgcolor: "#fff0f7", borderColor: "#FF4196", color: "#FF4196" },
                        transition: "0.15s" }}
                    />
                  ))}
              </Box>
            </Box>
          )}

          {/* SEO footer blurb */}
          <Box sx={{ mt: 4, p: 2.5, bgcolor: "#fff9fe", borderRadius: 2,
            border: "1px solid #fce4ec", textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#777", lineHeight: 1.8, fontSize: "0.88rem" }}>
              Watch <strong>{performer.name}</strong> live on InstaCamGirls — free chat, HD streaming,
              {performer.goldShow ? ' Gold Shows,' : ''} private shows and exclusive content.{' '}
              {performer.ethnicity && (
                <>Browse more <Link to={`/category/${performer.ethnicity.toLowerCase()}`}
                  style={{ color: "#FF4196", fontWeight: 600 }}>
                  {capitalize(performer.ethnicity)} cam girls
                </Link>{' '}</>
              )}
              or <Link to="/" style={{ color: "#FF4196", fontWeight: 600 }}>see who's live right now</Link>.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PerformerProfilePage;
